import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useFetcher, useRouteLoaderData } from '@remix-run/react';

import { usePageContext } from '~/context/usePageContext';
import useEmail from '~/hooks/useEmail';
import useToast, { type ToastData } from '~/hooks/useToast';
import type { RootLoaderData } from '~/root';

const useGatedSubscribe = ({ sendOtpAfterSubscribe = false } = {}) => {
  const { t } = useTranslation();
  const fetcher = useFetcher();
  const { visitToken, referralId } = usePageContext();
  const { site } = useRouteLoaderData('root') as RootLoaderData;
  const publication = site.publication;
  const isRecaptchaEnabled = publication.web_template.enable_recaptcha;
  const [, setEmail] = useEmail();

  const fetcherData = fetcher.data as { toast?: ToastData; otpRedirectUrl?: string } | undefined;
  useToast(fetcherData?.toast);

  useEffect(() => {
    if (fetcher.state === 'idle' && fetcherData?.otpRedirectUrl) {
      window.location.href = fetcherData.otpRedirectUrl;
    }
  }, [fetcher.state, fetcherData?.otpRedirectUrl]);

  const handleSubscribe = (email: string) => {
    const trimmedEmail = email.trim();
    setEmail(trimmedEmail);
    const formData = new FormData();
    formData.set('email', trimmedEmail);
    formData.set('is_js_enabled', 'true');
    formData.set('is_recaptcha_enabled', String(isRecaptchaEnabled));
    formData.set('subscribe_success_message', t('notifications.subscribe.subscribed'));
    formData.set('send_otp_after_subscribe', String(sendOtpAfterSubscribe));
    formData.set('redirect_path', typeof window !== 'undefined' ? window.location.pathname : '/');
    formData.set('auto_login_enabled', 'false');
    if (visitToken) formData.set('visit_token', visitToken);
    if (referralId) formData.set('ref', referralId);
    fetcher.submit(formData, { method: 'post', action: '/create' });
  };

  return { handleSubscribe, referralId };
};

export default useGatedSubscribe;
